import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart, MessageCircle, Share2, MapPin, Sparkles, Users, Baby, Wine, Home as HomeIcon,
  Ghost, Trophy, Loader2, Send, ImageIcon, X, Globe, UserCircle2, Star, Phone, Languages, Megaphone,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { StoryRing } from "@/components/StoryRing";

// Happiness tag palette + icon map
const HAPPY_TAGS = [
  { key: "mysterious_place", label: "Mysterious", icon: Ghost,     color: "from-indigo-600 to-purple-800" },
  { key: "must_visit",       label: "Must Visit", icon: Trophy,    color: "from-amber-500 to-orange-700" },
  { key: "unique",           label: "Unique",     icon: Sparkles,  color: "from-pink-500 to-fuchsia-700" },
  { key: "kids",             label: "Kids",       icon: Baby,      color: "from-sky-500 to-blue-700" },
  { key: "adult",            label: "Adult",      icon: Wine,      color: "from-rose-600 to-red-800" },
  { key: "family",           label: "Family",     icon: Users,     color: "from-emerald-500 to-teal-700" },
  { key: "other",            label: "Other",      icon: HomeIcon,  color: "from-stone-500 to-stone-800" },
];

const rupees = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;

const relTime = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export default function SocialFeed() {
  const { user } = useAuth();
  const { loc } = useUserLocation();
  const [posts, setPosts] = useState([]);
  const [scope, setScope] = useState("public");
  const [happyFilter, setHappyFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ scope });
      if (happyFilter) params.set("happiness", happyFilter);
      const r = await api.get(`/feed?${params}`);
      setPosts(r.data.items || []);
    } catch {
      toast.error("Couldn't load feed");
    } finally {
      setLoading(false);
    }
  }, [scope, happyFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 via-white to-stone-100" data-testid="social-feed">
      {/* Header */}
      <div className="max-w-2xl mx-auto px-4 md:px-6 pt-4 pb-2 sticky top-0 z-10 bg-white/85 backdrop-blur-md border-b border-stone-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="heading text-2xl md:text-3xl font-black text-stone-900">Feed</h1>
            <p className="text-xs text-stone-500">Share happiness · discover leads · build your community</p>
          </div>
          <Button
            onClick={() => setComposerOpen(true)}
            className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-sm"
            data-testid="feed-new-post-btn"
          >
            <Send size={14} className="mr-1.5" /> Post
          </Button>
        </div>
        {/* Scope tabs */}
        <div className="mt-3 flex gap-1 text-xs">
          {[
            { k: "public", l: "Global",     icon: Globe },
            { k: "favorites", l: "Favorites", icon: Heart },
            { k: "mine", l: "Mine",         icon: UserCircle2 },
          ].map(t => {
            const active = scope === t.k;
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setScope(t.k)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full font-bold ${active ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}
                data-testid={`feed-scope-${t.k}`}
              >
                <Icon size={12} /> {t.l}
              </button>
            );
          })}
        </div>
        {/* Happiness filter chips */}
        <div className="mt-2 flex gap-1.5 overflow-x-auto pb-2 no-scrollbar">
          <button
            onClick={() => setHappyFilter("")}
            className={`shrink-0 px-2.5 py-1 text-[11px] font-bold rounded-full ${happyFilter === "" ? "bg-[#E05E36] text-white" : "bg-stone-100 text-stone-600"}`}
            data-testid="feed-happy-all"
          >
            All
          </button>
          {HAPPY_TAGS.map(t => {
            const Icon = t.icon;
            const active = happyFilter === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setHappyFilter(active ? "" : t.key)}
                className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-full ${active ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}
                data-testid={`feed-happy-${t.key}`}
              >
                <Icon size={11} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Composer modal */}
      <AnimatePresence>
        {composerOpen && (
          <Composer
            user={user}
            loc={loc}
            onClose={() => setComposerOpen(false)}
            onCreated={(p) => { setPosts([p, ...posts]); setComposerOpen(false); }}
          />
        )}
      </AnimatePresence>

      {/* Feed */}
      <div className="max-w-2xl mx-auto px-0 md:px-6 pt-1">
        <StoryRing />
      </div>
      <div className="max-w-2xl mx-auto px-2 md:px-6 py-4 space-y-3">
        {loading ? (
          <div className="text-center py-16 text-stone-500"><Loader2 className="inline animate-spin mr-2" /> Loading…</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-stone-500">
            <Sparkles className="mx-auto mb-2 text-stone-400" size={28} />
            <div className="font-bold text-stone-700">No posts yet</div>
            <div className="text-xs mt-1">Be the first to share — tap Post above.</div>
          </div>
        ) : (
          posts.map(p => <PostCard key={p.post_id} post={p} onChange={load} />)
        )}
      </div>
    </div>
  );
}

// ---------------- Composer ----------------
function Composer({ user, loc, onClose, onCreated }) {
  const [body, setBody] = useState("");
  const [happy, setHappy] = useState("");
  const [attachLoc, setAttachLoc] = useState(true);
  const [image, setImage] = useState(null);
  const [posting, setPosting] = useState(false);
  const fileRef = useRef(null);

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Image too large (max 4MB)"); return; }
    const r = new FileReader();
    r.onload = () => setImage(r.result);
    r.readAsDataURL(f);
  };

  const submit = async () => {
    if (!body.trim() && !image) { toast.error("Write something or attach an image"); return; }
    setPosting(true);
    try {
      const payload = {
        body,
        images: image ? [image] : [],
        visibility: "public",
        happiness_tag: happy || undefined,
        city: loc?.city || undefined,
      };
      if (attachLoc && loc?.lat && loc?.lng) {
        payload.lat = loc.lat;
        payload.lng = loc.lng;
        payload.place_name = loc.city;
      }
      const r = await api.post("/feed/post", payload);
      toast.success("Posted");
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally {
      setPosting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm grid place-items-end md:place-items-center p-2 md:p-6" style={{ zIndex: "var(--z-modal)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }}
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-stone-200"
        onClick={e => e.stopPropagation()}
        data-testid="feed-composer"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-100">
          <div className="font-bold text-stone-900">New post</div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={18} /></button>
        </div>
        <div className="p-5">
          <div className="flex items-start gap-3">
            {user?.picture ? (
              <img loading="lazy" src={user.picture} alt="me" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-stone-300 grid place-items-center text-white font-bold">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="What's happening near you?"
              className="flex-1 resize-none outline-none min-h-[100px] text-sm md:text-base"
              data-testid="feed-composer-body"
            />
          </div>
          {image && (
            <div className="mt-3 relative rounded-2xl overflow-hidden border border-stone-200">
              <img loading="lazy" src={image} alt="attached" className="w-full max-h-64 object-cover" />
              <button
                onClick={() => setImage(null)}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white grid place-items-center"
              ><X size={14} /></button>
            </div>
          )}

          {/* Happiness tags */}
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1.5">Happiness tag (optional)</div>
            <div className="flex gap-1.5 flex-wrap">
              {HAPPY_TAGS.map(t => {
                const Icon = t.icon;
                const active = happy === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setHappy(active ? "" : t.key)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full border ${active ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"}`}
                    data-testid={`composer-happy-${t.key}`}
                  >
                    <Icon size={11} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Location toggle */}
          <label className="flex items-center gap-2 mt-3 text-xs text-stone-600">
            <input type="checkbox" checked={attachLoc} onChange={e => setAttachLoc(e.target.checked)} data-testid="composer-loc-toggle" />
            <MapPin size={12} /> Tag current location{loc?.city ? ` · ${loc.city}` : ""}
          </label>
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-stone-100 bg-stone-50 rounded-b-3xl">
          <div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickImage} />
            <button
              onClick={() => fileRef.current?.click()}
              className="text-stone-600 hover:text-[#E05E36] p-2 rounded-full"
              data-testid="composer-image-btn"
            ><ImageIcon size={18} /></button>
          </div>
          <Button onClick={submit} disabled={posting} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="composer-post-btn">
            {posting ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Posting…</> : <><Send size={14} className="mr-1.5" /> Post</>}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ---------------- Post card ----------------
function PostCard({ post, onChange }) {
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likes, setLikes] = useState(post.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState("");
  const [favUser, setFavUser] = useState(false);
  const [translated, setTranslated] = useState(null);
  const [translating, setTranslating] = useState(false);
  const happy = HAPPY_TAGS.find(t => t.key === post.happiness_tag);

  const translate = async () => {
    if (translated) { setTranslated(null); return; }              // toggle back to original
    if (!post.body?.trim()) return;
    setTranslating(true);
    try {
      // Default to browser lang, fallback to Hindi for Indian users.
      const target = (navigator.language || "en").split("-")[0];
      const r = await api.post("/ai/translate", { text: post.body, target_lang: target === "en" ? "hi" : target });
      setTranslated({ text: r.data.translated, lang: r.data.target_lang });
    } catch { toast.error("Translate failed"); }
    finally { setTranslating(false); }
  };

  // Heuristic — does this post look like a lead the user is trying to source?
  // Same phrasing family works across grooms, plumbers, party dresses, hotels, etc.
  const looksLikeLead = /\b(need|needed|require|looking\s+for|want\s+to|hire|book|find\s+me|urgent|help\s+with|top|best)\b/i.test(post.body || "");
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const broadcast = async () => {
    setBroadcasting(true);
    try {
      const { data } = await api.post("/leads/generate", {
        query: post.body,
        lat: post.location?.lat, lng: post.location?.lng, city: post.location?.city,
      });
      setBroadcastResult(data);
      toast.success(data.broadcast_count
        ? `Broadcast to ${data.broadcast_count} nearby providers (100 km)`
        : "No providers matched — try refining your post");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
    } finally { setBroadcasting(false); }
  };

  const toggleLike = async () => {
    try {
      const r = await api.post(`/feed/post/${post.post_id}/like`);
      setLiked(r.data.liked);
      setLikes((c) => c + (r.data.liked ? 1 : -1));
    } catch (e) {
      if (e?.response?.status === 401) toast.error("Sign in to like");
    }
  };

  const openComments = async () => {
    setShowComments(v => !v);
    if (!comments.length) {
      try {
        const r = await api.get(`/feed/post/${post.post_id}`);
        setComments(r.data.comments || []);
      } catch {}
    }
  };

  const addComment = async () => {
    if (!commentBody.trim()) return;
    try {
      const r = await api.post(`/feed/post/${post.post_id}/comment`, { body: commentBody });
      setComments((c) => [...c, r.data]);
      setCommentBody("");
    } catch (e) {
      if (e?.response?.status === 401) toast.error("Sign in to comment");
    }
  };

  const toggleFavUser = async () => {
    try {
      const r = await api.post("/favorites/toggle", { target_type: "user", target_id: post.author_id });
      setFavUser(r.data.favorited);
      toast.success(r.data.favorited ? "Added to Wishlist" : "Removed from Wishlist");
    } catch {}
  };

  const share = () => {
    const url = `${window.location.origin}/u/${post.author?.nvn || post.author_id}#p-${post.post_id}`;
    if (navigator.share) navigator.share({ title: "Find2Near post", url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success("Link copied"); }
  };

  const callAuthor = () => {
    if (post.author?.mobile) {
      window.location.href = `tel:${post.author.mobile}`;
      api.post("/ecall/log", {
        callee_user_id: post.author_id, callee_nvn: post.author?.nvn,
        callee_mobile: post.author.mobile, channel: "tel", direction: "outgoing",
      }).catch(() => {});
    } else {
      toast.error("Mobile not shared");
    }
  };

  const whatsappAuthor = () => {
    if (!post.author?.mobile) { toast.error("Mobile not shared"); return; }
    const num = String(post.author.mobile).replace(/\D/g, "");
    window.open(`https://wa.me/${num}?text=${encodeURIComponent("Hi, saw your post on Find2Near.")}`, "_blank");
    api.post("/ecall/log", {
      callee_user_id: post.author_id, callee_nvn: post.author?.nvn,
      callee_mobile: post.author.mobile, channel: "whatsapp", direction: "outgoing",
    }).catch(() => {});
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm"
      data-testid={`feed-post-${post.post_id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <Link to={`/u/${post.author?.nvn || post.author_id}`}>
          {post.author?.picture ? (
            <img loading="lazy" src={post.author.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E05E36] to-[#2D5A27] grid place-items-center text-white font-bold text-sm">
              {post.author?.name?.[0] || "U"}
            </div>
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Link to={`/u/${post.author?.nvn || post.author_id}`} className="font-bold text-sm text-stone-900 truncate hover:underline">
              {post.author?.name || "User"}
            </Link>
            {post.author?.nvn && (
              <span className="font-mono text-[10px] text-stone-500">NVN {post.author.nvn}</span>
            )}
          </div>
          <div className="text-[11px] text-stone-500 flex items-center gap-1.5 mt-0.5">
            <span>{relTime(post.created_at)}</span>
            {post.city && <><span>·</span><MapPin size={9} /> {post.city}</>}
            {happy && <><span>·</span><span className={`inline-flex items-center gap-1 text-[10px] font-bold text-white px-1.5 py-0.5 rounded-full bg-gradient-to-r ${happy.color}`}><happy.icon size={9} /> {happy.label}</span></>}
          </div>
        </div>
        <button
          onClick={toggleFavUser}
          className={`p-2 rounded-full ${favUser ? "text-rose-500 bg-rose-50" : "text-stone-400 hover:text-rose-500 hover:bg-rose-50"}`}
          title="Add author to Wishlist"
          data-testid={`feed-fav-author-${post.post_id}`}
        >
          <Star size={16} fill={favUser ? "currentColor" : "none"} />
        </button>
      </div>

      {/* Body */}
      {post.body && (
        <div className="px-4 pb-1 text-sm text-stone-800 whitespace-pre-wrap">{translated?.text || post.body}</div>
      )}
      {post.body && (
        <div className="px-4 pb-3 flex items-center gap-3 flex-wrap">
          <button
            onClick={translate}
            disabled={translating}
            className="text-[11px] font-bold text-stone-500 hover:text-[#E05E36] inline-flex items-center gap-1"
            data-testid={`feed-translate-${post.post_id}`}
          >
            {translating ? <Loader2 size={11} className="animate-spin" /> : <Languages size={11} />}
            {translated ? "Show original" : "Translate this"}
            {translated && <span className="text-[10px] text-stone-400"> · {translated.lang}</span>}
          </button>
          {looksLikeLead && !broadcastResult && (
            <button
              onClick={broadcast}
              disabled={broadcasting}
              className="text-[11px] font-bold text-[#E05E36] hover:text-[#c94e2a] inline-flex items-center gap-1"
              data-testid={`feed-broadcast-${post.post_id}`}
            >
              {broadcasting ? <Loader2 size={11} className="animate-spin" /> : <Megaphone size={11} />}
              Broadcast to nearby providers (100 km)
            </button>
          )}
          {broadcastResult && (
            <span className="text-[11px] font-semibold text-emerald-700 inline-flex items-center gap-1" data-testid={`feed-broadcast-result-${post.post_id}`}>
              <Megaphone size={11} /> Sent to {broadcastResult.broadcast_count} providers
            </span>
          )}
        </div>
      )}
      {post.images?.[0] && (
        <img loading="lazy" src={post.images[0]} alt="" className="w-full max-h-[520px] object-cover" />
      )}

      {/* Catalog items */}
      {post.catalog_items?.length > 0 && (
        <div className="px-4 py-3 grid grid-cols-2 gap-2 border-t border-stone-100">
          {post.catalog_items.slice(0, 4).map(it => (
            <Link key={it.item_id} to={`/store/${post.author?.nvn || post.author_id}#${it.item_id}`} className="flex gap-2 items-center bg-stone-50 rounded-xl p-2 hover:bg-stone-100">
              {it.image ? <img loading="lazy" src={it.image} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-stone-200" />}
              <div className="min-w-0">
                <div className="text-xs font-bold text-stone-800 truncate">{it.name}</div>
                <div className="text-xs text-[#E05E36] font-black">{rupees(it.price_paise)}</div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between px-2 py-2 border-t border-stone-100 text-xs">
        <button
          onClick={toggleLike}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg hover:bg-stone-50 ${liked ? "text-rose-500 font-bold" : "text-stone-600"}`}
          data-testid={`feed-like-${post.post_id}`}
        >
          <Heart size={16} fill={liked ? "currentColor" : "none"} /> {likes || ""} Like
        </button>
        <button
          onClick={openComments}
          className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg hover:bg-stone-50 text-stone-600"
          data-testid={`feed-comment-${post.post_id}`}
        >
          <MessageCircle size={16} /> {post.comments_count || ""} Comment
        </button>
        <button
          onClick={callAuthor}
          className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg hover:bg-stone-50 text-stone-600"
          data-testid={`feed-call-${post.post_id}`}
        >
          <Phone size={16} /> Call
        </button>
        <button
          onClick={whatsappAuthor}
          className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg hover:bg-stone-50 text-emerald-600"
          data-testid={`feed-wa-${post.post_id}`}
        >
          <Send size={16} /> WhatsApp
        </button>
        <button
          onClick={share}
          className="hidden md:inline-flex flex-1 items-center justify-center gap-1.5 py-2 rounded-lg hover:bg-stone-50 text-stone-600"
          data-testid={`feed-share-${post.post_id}`}
        >
          <Share2 size={16} /> Share
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="border-t border-stone-100 bg-stone-50 px-4 py-3 space-y-2">
          {comments.map(c => (
            <div key={c.comment_id} className="flex items-start gap-2 text-sm">
              <div className="w-7 h-7 rounded-full bg-stone-300 grid place-items-center text-white text-xs font-bold shrink-0">
                {c.user_name?.[0] || "U"}
              </div>
              <div className="bg-white rounded-xl px-3 py-1.5 border border-stone-200 flex-1">
                <div className="text-[11px] font-bold text-stone-700">{c.user_name}</div>
                <div className="text-sm text-stone-800">{c.body}</div>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <input
              value={commentBody}
              onChange={e => setCommentBody(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addComment()}
              placeholder="Write a comment…"
              className="flex-1 bg-white border border-stone-200 rounded-full px-4 py-2 text-sm outline-none focus:border-[#E05E36]"
              data-testid={`feed-comment-input-${post.post_id}`}
            />
            <button onClick={addComment} className="rounded-full bg-[#E05E36] text-white p-2" data-testid={`feed-comment-submit-${post.post_id}`}>
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
}
